import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { CuesApplied } from 'services/eventTrackingServiceTypes';

export type CueType = 'tempo' | 'pitch' | 'loudness';

export type CueAPI = {
  setCue(type: CueType, value: string): void;
  removeCue(type: CueType): void;
  getCueValue(type: CueType): number | 'mixed';
  getEditorStateFormattedText(): string;
  setMultipleCues(cues: Record<CueType, string>): void;
  selectAllText(): void;
  getPlainText(): string;
  getCuesApplied(): CuesApplied;
  isPauseCueTokenSelected(): boolean;
  setPauseCue(value: string): void;
  getPauseCueValue(): number | null;
  removePauseCue(): void;
  clearCues(): void;
  removeReplacements(): void;
  clearMarks(options?: { cues?: boolean; replacements?: boolean }): void;
  hasReplacements(): boolean;
  hasCues(): boolean;
  subscribeToContentChange(
    listener: (formattedText: string) => void
  ): () => void;
};

export type SectionEditorAPI = {
  cue: CueAPI;
};

type Bridge = {
  registerApi(sectionId: string, api: SectionEditorAPI): void;
  unregisterApi(sectionId: string): void;
  getApi(sectionId?: string | null): SectionEditorAPI | undefined;
};

const EditorBridgeContext = createContext<Bridge | null>(null);

/**
 * Provides a registry system for managing multiple section editors
 * and their cue APIs. It allows sidebar (or other components) to access and control cue operations
 * (tempo, pitch, loudness) across different editor instances - like what we have in ScriptStudio.
 *
 * Use:
 * 1. Upper level component wraps with <EditorBridgeProvider>
 * 2. Each section editor registers its API: registerApi(sectionId, { cue: cueAPI }) - See SectionTextEditor.tsx
 * 3. Components use like so:
 * const { getApi }: Bridge = useEditorBridge();
 * const editorApi: SectionEditorAPI = getApi(sectionId);
 * editorApi?.cue.setCue('tempo', '120');
 */
export function EditorBridgeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const registryRef = useRef(new Map<string, SectionEditorAPI>());

  const registerApi = useCallback(
    (sectionId: string, api: SectionEditorAPI) => {
      registryRef.current.set(sectionId, api);
    },
    []
  );

  const unregisterApi = useCallback((sectionId: string) => {
    registryRef.current.delete(sectionId);
  }, []);

  const getApi = useCallback((sectionId?: string | null) => {
    if (!sectionId) return undefined;
    return registryRef.current.get(sectionId);
  }, []);

  const value = useMemo(
    () => ({ registerApi, unregisterApi, getApi }),
    [registerApi, unregisterApi, getApi]
  );

  return (
    <EditorBridgeContext.Provider value={value}>
      {children}
    </EditorBridgeContext.Provider>
  );
}

export function useEditorBridge() {
  const ctx = useContext(EditorBridgeContext);
  if (!ctx)
    throw new Error('useEditorBridge must be used within EditorBridgeProvider');
  return ctx;
}
